import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  repositorioAlarmas,
  type FiltroEstadoAlarma,
  type FiltrosAlarmas,
} from '../../../data/repositories/RepositorioAlarmas';
import type { CriticidadValor } from '../../../domain/value-objects/Criticidad';
import { ErrorApp, registrarError } from '../../../domain/errores';
import { useCascadaFiltros } from '../../hooks/useCascadaFiltros';
import { useTiempoReal } from '../../hooks/useTiempoReal';

/** Períodos rápidos que ofrece la pantalla. */
export type Periodo = 'todo' | '1h' | '24h' | '7d' | '30d';

const HORAS_POR_PERIODO: Record<Exclude<Periodo, 'todo'>, number> = {
  '1h': 1,
  '24h': 24,
  '7d': 24 * 7,
  '30d': 24 * 30,
};

const POR_PAGINA = 25;

function calcularDesde(periodo: Periodo): Date | null {
  if (periodo === 'todo') return null;
  return new Date(Date.now() - HORAS_POR_PERIODO[periodo] * 60 * 60 * 1000);
}

/**
 * Estado y datos de la pantalla de alarmas.
 *
 * Separar esto del componente permite que la pantalla se ocupe sólo de pintar,
 * y deja la lógica de filtros, paginación y refresco en un sitio que se puede
 * leer entero de una vez.
 */
export function useAlarmas() {
  const clienteConsultas = useQueryClient();
  const cascada = useCascadaFiltros({}, 'alarmas');

  const [estado, setEstado] = useState<FiltroEstadoAlarma>('abiertas');
  const [criticidades, setCriticidades] = useState<CriticidadValor[]>([]);
  const [tipoUmbral, setTipoUmbral] = useState<'analogico' | 'digital' | null>(null);
  const [periodo, setPeriodo] = useState<Periodo>('todo');
  const [pagina, setPagina] = useState(1);
  const [recientes, setRecientes] = useState<ReadonlySet<string>>(new Set());

  const filtros: FiltrosAlarmas = useMemo(
    () => ({
      estado,
      clienteId: cascada.seleccion.clienteId,
      areaId: cascada.seleccion.areaId,
      nodoId: cascada.seleccion.nodoId,
      criticidades,
      tipoUmbral,
      desde: calcularDesde(periodo),
      pagina,
      porPagina: POR_PAGINA,
    }),
    [estado, cascada.seleccion, criticidades, tipoUmbral, periodo, pagina],
  );

  const consulta = useQuery({
    queryKey: ['alarmas', filtros],
    queryFn: () => repositorioAlarmas.listar(filtros),
  });

  const resumen = useQuery({
    queryKey: ['alarmas', 'resumen', cascada.seleccion.clienteId],
    queryFn: () => repositorioAlarmas.contarAbiertasPorCriticidad(cascada.seleccion.clienteId),
  });

  const refrescar = useCallback(() => {
    void clienteConsultas.invalidateQueries({ queryKey: ['alarmas'] });
  }, [clienteConsultas]);

  /**
   * Cualquier cambio en la tabla de alarmas obliga a releer la página actual:
   * el aviso trae la fila cruda, sin los nombres de sensor, nodo y área que la
   * pantalla necesita, así que no se puede insertar directamente.
   */
  useTiempoReal({ tabla: 'alarmas', evento: '*' }, (payload) => {
    const registro = (payload.new ?? {}) as { id?: unknown };
    const id = registro.id ? String(registro.id) : null;

    if (id && payload.eventType === 'INSERT') {
      // Se resalta un momento para que el operador vea qué acaba de entrar.
      setRecientes((previas) => new Set(previas).add(id));
      setTimeout(() => {
        setRecientes((previas) => {
          const copia = new Set(previas);
          copia.delete(id);
          return copia;
        });
      }, 6000);
    }

    refrescar();
  });

  const reconocer = useMutation({
    mutationFn: (id: string) => repositorioAlarmas.reconocer(id),
    onSuccess: () => {
      toast.success('Alarma reconocida');
      refrescar();
    },
    onError: (e) => {
      registrarError('alarmas.reconocer', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo reconocer la alarma');
    },
  });

  const resolver = useMutation({
    mutationFn: (id: string) => repositorioAlarmas.resolver(id),
    onSuccess: () => {
      toast.success('Alarma resuelta');
      refrescar();
    },
    onError: (e) => {
      registrarError('alarmas.resolver', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo resolver la alarma');
    },
  });

  /** Alterna una criticidad en el filtro y vuelve a la primera página. */
  const alternarCriticidad = useCallback((valor: CriticidadValor) => {
    setPagina(1);
    setCriticidades((previas) =>
      previas.includes(valor) ? previas.filter((c) => c !== valor) : [...previas, valor],
    );
  }, []);

  const limpiarFiltros = useCallback(() => {
    cascada.limpiar();
    setEstado('abiertas');
    setCriticidades([]);
    setTipoUmbral(null);
    setPeriodo('todo');
    setPagina(1);
  }, [cascada]);

  return {
    cascada,
    alarmas: consulta.data?.filas ?? [],
    total: consulta.data?.total ?? 0,
    cargando: consulta.isLoading,
    error: consulta.isError,
    recargando: consulta.isFetching,
    resumen: resumen.data ?? { critica: 0, alta: 0, media: 0, baja: 0 },
    recientes,

    estado,
    criticidades,
    tipoUmbral,
    periodo,
    pagina,
    porPagina: POR_PAGINA,

    cambiarEstado: (v: FiltroEstadoAlarma) => {
      setEstado(v);
      setPagina(1);
    },
    alternarCriticidad,
    cambiarTipoUmbral: (v: 'analogico' | 'digital' | null) => {
      setTipoUmbral(v);
      setPagina(1);
    },
    cambiarPeriodo: (v: Periodo) => {
      setPeriodo(v);
      setPagina(1);
    },
    cambiarPagina: setPagina,
    limpiarFiltros,
    refrescar,

    reconocer: (id: string) => reconocer.mutate(id),
    resolver: (id: string) => resolver.mutate(id),
    procesando: reconocer.isPending || resolver.isPending,
  };
}
